import { Axios } from "api/axios";
import { useFormik } from "formik";
import React, { useEffect, useState, useCallback } from "react";
import { Col, Form, Row } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import * as Yup from "yup";
import StockTransferInwardTableForEdit from "../ConfigureItemsTables/StockTransferInwardTableForEdit";
import { api } from "helpers/api_url";
import PageContainer from "components/PageContainer/PageContainer";
import FormHeader from "components/Form&DetailsHeader/Form&DetailsHeader";
import SubmitButton from "components/Buttons/SubmitButton";
import CancelBackButton from "components/Buttons/CancelBackButton";
import { showConfirm } from "utils/swalHelper";
import { Autocomplete } from "@mui/material";
import BootstrapTextField from "Common/BootstrapTextField";
import { autocompleteDropdownStyles } from "Constants/Style";
import { fetchWarehouseDropdown } from "slices/Master/Warehouse/thunk";
import LoadingSpinner from "components/LoaderErrorWrapper/LoadingSpinner";
import { useAutoMinimizeSidebar } from "CustomHooks/useAutoMinimizeSidebar";

interface GRNFormValues {
  dispatch_no: string;
  inward_date: string;
  transfer_from: string;
  transfer_from_id: string;
  transfer_to: string;
  transfer_to_id: string;
  upload_document: File | null;
  existing_document_file: string | null;
  remark: string;
  received_by?: string;
  received_by_user_id?: string;
}

interface ScannedItem {
  dispatch_item_id: string;
  item_id: string;
  inventory_item_id: string;
  bag_no: string;
  received_quantity: number;
  damaged_quantity?: number;
  selling_price?: number;
  remark?: string;
}

const EditStockTransferGRN: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch<any>();
  const params = useParams<{ id?: string }>();
  const inventoryId = params.id;

  const [dispatchItems, setDispatchItems] = useState<any[]>([]);
  const [scannedItems, setScannedItems] = useState<ScannedItem[]>([]);
  const [isLoadingInitialData, setIsLoadingInitialData] = useState(true);
  const [initialDataLoaded, setInitialDataLoaded] = useState(false);
  const [warehouseOptions, setWarehouseOptions] = useState<any[]>([]);
  const [userOptions, setUserOptions] = useState<any[]>([]);

  const user = useSelector((state: any) => state.Login.user);
  const currentUserName = user
    ? user.first_name && user.last_name
      ? `${user.first_name} ${user.last_name}`
      : user.username || ""
    : "";
  const currentUserId = user?.user_id || user?.id || "";

  // Get today's date in YYYY-MM-DD format for date input
  const getTodayDate = () => {
    const today = new Date();
    return today.toISOString().split("T")[0];
  };

  // Auto-minimize sidebar on tablet screens
  useAutoMinimizeSidebar();

  const validationSchema = Yup.object({
    dispatch_no: Yup.string().required("Dispatch number is required"),
    inward_date: Yup.string().required("Please select Inward Date"),
    transfer_from: Yup.string().required("Transfer from is required"),
    transfer_to: Yup.string().required("Transfer to is required"),
    remark: Yup.string(),
    upload_document: Yup.mixed().nullable(),
  });

  const formik = useFormik<GRNFormValues>({
    initialValues: {
      dispatch_no: "",
      inward_date: getTodayDate(),
      transfer_from: "",
      transfer_from_id: "",
      transfer_to: "",
      transfer_to_id: "",
      upload_document: null,
      existing_document_file: null,
      remark: "",
      received_by: currentUserName,
      received_by_user_id: currentUserId,
    },
    validationSchema: validationSchema,
    onSubmit: async (values) => {
      // Validate scanned items
      // if (scannedItems.length === 0) {
      //   toast.error("Please scan at least one item");
      //   return;
      // }

      // Build FormData
      const formData = new FormData();
      formData.append("inward_date", values.inward_date);
      formData.append("transfer_to_warehouse_id", values.transfer_to_id);
      formData.append("received_by", values.received_by || "");
      formData.append("received_by_user_id", values.received_by_user_id || "");
      formData.append("remarks", values.remark || "");

      if (values.upload_document && values.upload_document instanceof File) {
        formData.append("document_file", values.upload_document);
      }

      // Transform scanned items to grn_items format
      const grnItems = scannedItems.map((item) => ({
        dispatch_item_id: item.dispatch_item_id,
        item_id: item.item_id,
        received_quantity: item.received_quantity,
        damaged_quantity: item.damaged_quantity || 0,
        selling_price: item.selling_price || undefined,
        remark: item.remark || undefined,
        grn_items_details: [
          {
            quantity: item.received_quantity,
            selling_price: item.selling_price || undefined,
            remark: item.remark || undefined,
          },
        ],
      }));

      formData.append("grn_items", JSON.stringify(grnItems));

      try {
        await Axios.put(
          `${api.warehouse2.pendingStockTransfer.update}${inventoryId}/`,
          formData,
          {
            headers: {
              "Content-Type": "multipart/form-data",
            },
          }
        );

        toast.success("Stock transfer inward (GRN) updated successfully");
        navigate("/warehouse-item-inward");
      } catch (err: any) {
        const errorMessage =
          err?.response?.data?.message ||
          err?.response?.data?.error ||
          "Failed to update stock transfer inward";
        toast.error(errorMessage);
        console.error("Error updating stock transfer inward:", err);
      }
    },
  });

  const { setFieldValue } = formik;

  console.log("Formik values:", scannedItems);
  // Fetch GRN data for edit
  useEffect(() => {
    const fetchGRNData = async () => {
      if (!inventoryId || initialDataLoaded) {
        return;
      }

      setIsLoadingInitialData(true);
      try {
        const response = await Axios.get(
          `${api.warehouse2.pendingStockTransfer.details}${inventoryId}`
        );
        const grnDataResult = response?.data?.data;

        if (grnDataResult) {
          // Prefill form fields with GRN data
          setFieldValue(
            "dispatch_no",
            grnDataResult.dispatch?.dispatch_number || ""
          );
          setFieldValue(
            "transfer_from",
            grnDataResult.dispatch?.transfer_from_warehouse?.warehouse_name ||
              ""
          );
          setFieldValue(
            "transfer_from_id",
            grnDataResult.dispatch?.transfer_from_warehouse_id || ""
          );
          setFieldValue(
            "transfer_to",
            grnDataResult.warehouse?.warehouse_name || ""
          );
          setFieldValue("transfer_to_id", grnDataResult.warehouse_id || "");
          setFieldValue(
            "inward_date",
            grnDataResult.inward_date
              ? new Date(grnDataResult.inward_date).toISOString().split("T")[0]
              : getTodayDate()
          );
          setFieldValue("remark", grnDataResult.remarks || "");
          setFieldValue(
            "existing_document_file",
            grnDataResult.document_file || null
          );
          setFieldValue(
            "received_by",
            grnDataResult.received_by_details
              ? `${grnDataResult.received_by_details.first_name || ""} ${
                  grnDataResult.received_by_details.last_name || ""
                }`.trim() || grnDataResult.received_by_details.username
              : currentUserName
          );
          setFieldValue(
            "received_by_user_id",
            grnDataResult.received_by || currentUserId
          );

          // Transform dispatch items for table
          if (grnDataResult.dispatch?.dispatch_items) {
            const items = grnDataResult.dispatch.dispatch_items.map(
              (item: any) => {
                // Check which bags are already in inventory_items (scanned/verified)
                const verifiedBagIds = new Set(
                  grnDataResult.inventory_items?.map(
                    (inv: any) => inv.inventory_item_id
                  ) || []
                );

                const updatedBags = (item.bags || []).map((b: any) => ({
                  ...b,
                  status: verifiedBagIds.has(b.inventory_item_id)
                    ? "verified"
                    : "not_verified",
                }));

                return {
                  ...item,
                  bags: updatedBags,
                };
              }
            );
            setDispatchItems(items);
          }

          // Transform inventory items to scanned items
          if (grnDataResult.inventory_items) {
            const scanned: ScannedItem[] = [];
            grnDataResult.inventory_items.forEach((invItem: any) => {
              // Find corresponding dispatch item
              const dispatchItem = grnDataResult.dispatch?.dispatch_items?.find(
                (di: any) => di.master_item_id === invItem.master_item_id
              );

              if (dispatchItem) {
                scanned.push({
                  dispatch_item_id: dispatchItem.dispatch_item_id,
                  item_id: invItem.master_item_id,
                  inventory_item_id: invItem.inventory_item_id,
                  bag_no: invItem.bag_no || "",
                  received_quantity: invItem.quantity || 0,
                  damaged_quantity: 0,
                  selling_price: invItem.selling_price || undefined,
                  remark: invItem.remark || undefined,
                });
              }
            });
            setScannedItems(scanned);
          }

          setInitialDataLoaded(true);
        } else {
          toast.error("GRN data not found");
          navigate("/warehouse-item-inward");
          setIsLoadingInitialData(false);
        }
      } catch (error: any) {
        console.error("Error fetching GRN data:", error);
        toast.error(error?.message || "Failed to load GRN data");
        navigate("/warehouse-item-inward");
      } finally {
        setIsLoadingInitialData(false);
      }
    };

    if (inventoryId && !initialDataLoaded) {
      fetchGRNData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inventoryId, initialDataLoaded, navigate, setFieldValue]);

  // Fetch warehouse options
  useEffect(() => {
    const fetchWarehouseOptions = async () => {
      try {
        const response = await dispatch(fetchWarehouseDropdown()).unwrap();
        setWarehouseOptions(response?.data || []);
      } catch (error) {
        console.error("Error fetching warehouse options:", error);
      }
    };
    fetchWarehouseOptions();
  }, [dispatch]);

  // Fetch user options for Received By field
  useEffect(() => {
    const fetchUserOptions = async () => {
      try {
        const response = await Axios.get("/users/dropdown/all");
        setUserOptions(response?.data?.data || []);
      } catch (error) {
        console.error("Error fetching user options:", error);
        setUserOptions([]);
      }
    };
    fetchUserOptions();
  }, []);

  const handleItemsScanned = useCallback((items: ScannedItem[]) => {
    setScannedItems(items);
  }, []);

  return (
    <PageContainer>
      <FormHeader
        title="Edit Stock Transfer Inward"
        firstLabel="Stock Transfer Inward"
        secondLabel="Edit GRN"
        handleArrowClick={() => navigate("/warehouse-item-inward")}
      />

      {isLoadingInitialData ? (
        <LoadingSpinner />
      ) : (
        <Form autoComplete="off" onSubmit={formik.handleSubmit}>
          <Row>
            <Col lg={3}>
              <div className="mb-3">
                <Form.Label>
                  Dispatch No. <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  type="text"
                  name="dispatch_no"
                  value={formik.values.dispatch_no}
                  disabled
                  style={{ backgroundColor: "#f5f5f5" }}
                />
              </div>
            </Col>

            <Col lg={3}>
              <div className="mb-3">
                <Form.Label>
                  Inward Date <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  type="date"
                  name="inward_date"
                  value={formik.values.inward_date}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  isInvalid={
                    !!formik.errors.inward_date && formik.touched.inward_date
                  }
                />
                <Form.Control.Feedback type="invalid">
                  {formik.errors.inward_date}
                </Form.Control.Feedback>
              </div>
            </Col>

            <Col lg={3}>
              <div className="mb-3">
                <Form.Label>
                  Transfer From <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  type="text"
                  name="transfer_from"
                  value={formik.values.transfer_from}
                  disabled
                  style={{ backgroundColor: "#f5f5f5" }}
                />
              </div>
            </Col>

            <Col lg={3}>
              <div className="mb-3">
                <Form.Label>
                  Transfer To <span className="text-danger">*</span>
                </Form.Label>
                <Autocomplete
                  options={warehouseOptions}
                  getOptionLabel={(option: any) => option?.warehouse_name || ""}
                  value={
                    warehouseOptions.find(
                      (opt: any) =>
                        opt.warehouse_id === formik.values.transfer_to_id
                    ) || null
                  }
                  onChange={(event, selectedOption: any) => {
                    formik.setFieldValue(
                      "transfer_to_id",
                      selectedOption?.warehouse_id || ""
                    );
                    formik.setFieldValue(
                      "transfer_to",
                      selectedOption?.warehouse_name || ""
                    );
                    if (selectedOption?.warehouse_name) {
                      formik.setFieldError("transfer_to", undefined);
                    }
                  }}
                  onBlur={() => formik.setFieldTouched("transfer_to", true)}
                  renderInput={(params) => (
                    <BootstrapTextField
                      {...params}
                      placeholder="Select Warehouse"
                      name="transfer_to"
                      error={
                        formik.touched.transfer_to &&
                        Boolean(formik.errors.transfer_to)
                      }
                      helperText={
                        formik.touched.transfer_to && formik.errors.transfer_to
                      }
                      fullWidth
                      size="small"
                      sx={{
                        background: "#FFF",
                        ...autocompleteDropdownStyles.inputSx,
                        "& .MuiOutlinedInput-root": {
                          "& fieldset": {
                            borderColor: "#ced4da",
                          },
                          "&:hover fieldset": {
                            borderColor: "#ced4da",
                          },
                          "&.Mui-focused fieldset": {
                            borderColor: "#ced4da",
                          },
                        },
                      }}
                    />
                  )}
                  renderOption={(props, option) =>
                    autocompleteDropdownStyles.renderOption(
                      props,
                      option,
                      (opt: any) => opt?.warehouse_name || ""
                    )
                  }
                  ListboxProps={autocompleteDropdownStyles.ListboxProps}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      "& fieldset": {
                        borderColor: "#ced4da",
                      },
                      "&:hover fieldset": {
                        borderColor: "#ced4da",
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: "#ced4da",
                      },
                    },
                  }}
                />
              </div>
            </Col>

            {/* Received By */}
            <Col lg={3}>
              <div className="mb-3">
                <Form.Label>Received By</Form.Label>
                <Autocomplete
                  options={userOptions}
                  getOptionLabel={(option: any) =>
                    option?.first_name && option?.last_name
                      ? `${option.first_name} ${option.last_name}`
                      : option?.username || option?.email || ""
                  }
                  value={
                    userOptions.find(
                      (opt: any) =>
                        opt.user_id === formik.values.received_by_user_id
                    ) || null
                  }
                  onChange={(event, selectedOption: any) => {
                    formik.setFieldValue(
                      "received_by_user_id",
                      selectedOption?.user_id || ""
                    );
                    formik.setFieldValue(
                      "received_by",
                      selectedOption?.first_name && selectedOption?.last_name
                        ? `${selectedOption.first_name} ${selectedOption.last_name}`
                        : selectedOption?.username || ""
                    );
                  }}
                  renderInput={(params) => (
                    <BootstrapTextField
                      {...params}
                      placeholder="Select User"
                      name="received_by"
                      fullWidth
                      size="small"
                      sx={{
                        background: "#FFF",
                        ...autocompleteDropdownStyles.inputSx,
                        "& .MuiOutlinedInput-root": {
                          "& fieldset": {
                            borderColor: "#ced4da",
                          },
                          "&:hover fieldset": {
                            borderColor: "#ced4da",
                          },
                          "&.Mui-focused fieldset": {
                            borderColor: "#ced4da",
                          },
                        },
                      }}
                    />
                  )}
                  renderOption={(props, option) =>
                    autocompleteDropdownStyles.renderOption(
                      props,
                      option,
                      (opt: any) =>
                        opt?.first_name && opt?.last_name
                          ? `${opt.first_name} ${opt.last_name}`
                          : opt?.username || opt?.email || ""
                    )
                  }
                  ListboxProps={autocompleteDropdownStyles.ListboxProps}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      "& fieldset": {
                        borderColor: "#ced4da",
                      },
                      "&:hover fieldset": {
                        borderColor: "#ced4da",
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: "#ced4da",
                      },
                    },
                  }}
                />
              </div>
            </Col>

            {/* Remarks */}
            <Col lg={3}>
              <div className="mb-3">
                <Form.Label>Remark</Form.Label>
                <Form.Control
                  type="text"
                  name="remark"
                  placeholder="Enter Remark"
                  value={formik.values.remark}
                  onChange={formik.handleChange}
                />
              </div>
            </Col>

            {/* Document */}
            {/* Document */}
            <Col lg={3}>
              <div className="mb-3">
                <Form.Label>
                  Upload document
                  {/* <span className="text-danger">*</span> */}
                </Form.Label>

                {/* Custom styled box */}
                <div
                  className={`d-flex align-items-center justify-content-between p-2 border rounded ${
                    formik.errors.upload_document &&
                    formik.touched.upload_document
                      ? "border-danger"
                      : ""
                  }`}
                  style={{
                    height: "40px",
                    cursor: "pointer",
                    transition: "all 0.2s ease",
                    backgroundColor: "#fff",
                    borderColor:
                      formik.errors.upload_document &&
                      formik.touched.upload_document
                        ? "#dc3545"
                        : "#ced4da",
                  }}
                  onMouseEnter={(e) => {
                    if (
                      !formik.errors.upload_document ||
                      !formik.touched.upload_document
                    ) {
                      e.currentTarget.style.borderColor = "#80bdff";
                      e.currentTarget.style.backgroundColor = "#f8f9fa";
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (
                      !formik.errors.upload_document ||
                      !formik.touched.upload_document
                    ) {
                      e.currentTarget.style.borderColor = "#ced4da";
                      e.currentTarget.style.backgroundColor = "#fff";
                    }
                  }}
                  onClick={() => {
                    document.getElementById("uploadDocInput")?.click();
                  }}
                >
                  <span
                    className="text-muted"
                    style={{
                      flex: 1,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      marginRight: "8px",
                      minWidth: 0,
                    }}
                    title={
                      formik.values.upload_document instanceof File
                        ? formik.values.upload_document.name
                        : formik.values.existing_document_file
                        ? formik.values.existing_document_file
                            .split("/")
                            .pop() || "Existing Document"
                        : "Click to upload document"
                    }
                  >
                    {formik.values.upload_document instanceof File ? (
                      formik.values.upload_document.name
                    ) : formik.values.existing_document_file ? (
                      <a
                        href={formik.values.existing_document_file}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        className="text-decoration-none text-primary"
                        style={{
                          cursor: "pointer",
                          display: "inline-block",
                          maxWidth: "100%",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                        title={
                          formik.values.existing_document_file
                            .split("/")
                            .pop() || "Existing Document"
                        }
                      >
                        {formik.values.existing_document_file
                          .split("/")
                          .pop() || "Existing Document"}
                      </a>
                    ) : (
                      "Click to upload document"
                    )}
                  </span>

                  {/* Action buttons */}
                  <div
                    className="d-flex align-items-center gap-2"
                    onClick={(e) => e.stopPropagation()}
                    style={{ flexShrink: 0 }}
                  >
                    {formik.values.upload_document instanceof File ? (
                      <>
                        <i
                          className="bi bi-upload text-primary"
                          style={{
                            cursor: "pointer",
                            fontSize: "1rem",
                            transition: "transform 0.2s ease",
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.transform = "scale(1.1)";
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.transform = "scale(1)";
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                            document.getElementById("uploadDocInput")?.click();
                          }}
                          title="Change document"
                        ></i>
                        <i
                          className="bi bi-x-circle text-danger"
                          style={{
                            cursor: "pointer",
                            transition: "transform 0.2s ease",
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.transform = "scale(1.1)";
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.transform = "scale(1)";
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                            formik.setFieldValue("upload_document", null);
                            formik.setFieldValue(
                              "existing_document_file",
                              null
                            );
                            // Reset file input
                            const fileInput = document.getElementById(
                              "uploadDocInput"
                            ) as HTMLInputElement;
                            if (fileInput) {
                              fileInput.value = "";
                            }
                          }}
                          title="Remove document"
                        ></i>
                      </>
                    ) : formik.values.existing_document_file ? (
                      <>
                        <a
                          href={formik.values.existing_document_file}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(e) => e.stopPropagation()}
                          title="View document"
                        >
                          <i
                            className="bi bi-eye text-primary"
                            style={{
                              cursor: "pointer",
                              fontSize: "1rem",
                              transition: "transform 0.2s ease",
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.transform = "scale(1.1)";
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.transform = "scale(1)";
                            }}
                          ></i>
                        </a>
                        <i
                          className="bi bi-upload text-primary"
                          style={{
                            cursor: "pointer",
                            fontSize: "1rem",
                            transition: "transform 0.2s ease",
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.transform = "scale(1.1)";
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.transform = "scale(1)";
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                            document.getElementById("uploadDocInput")?.click();
                          }}
                          title="Change document"
                        ></i>
                        <i
                          className="bi bi-x-circle text-danger"
                          style={{
                            cursor: "pointer",
                            transition: "transform 0.2s ease",
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.transform = "scale(1.1)";
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.transform = "scale(1)";
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                            formik.setFieldValue("upload_document", null);
                            formik.setFieldValue(
                              "existing_document_file",
                              null
                            );
                            // Reset file input
                            const fileInput = document.getElementById(
                              "uploadDocInput"
                            ) as HTMLInputElement;
                            if (fileInput) {
                              fileInput.value = "";
                            }
                          }}
                          title="Remove document"
                        ></i>
                      </>
                    ) : (
                      <i
                        className="bi bi-upload"
                        style={{
                          cursor: "pointer",
                          fontSize: "1rem",
                          color: "#6c757d",
                          transition: "all 0.2s ease",
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.color = "#007bff";
                          e.currentTarget.style.transform = "scale(1.1)";
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.color = "#6c757d";
                          e.currentTarget.style.transform = "scale(1)";
                        }}
                      ></i>
                    )}
                  </div>
                </div>

                {/* Hidden real file input */}
                <Form.Control
                  id="uploadDocInput"
                  type="file"
                  name="upload_document"
                  style={{ display: "none" }}
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                    const file = event.currentTarget.files?.[0] || null;
                    formik.setFieldValue("upload_document", file);
                    // Clear existing_document_file when a new file is uploaded
                    if (file) {
                      formik.setFieldValue("existing_document_file", null);
                    }
                    formik.setFieldTouched("upload_document", true);
                  }}
                  onBlur={formik.handleBlur}
                  isInvalid={
                    !!formik.errors.upload_document &&
                    formik.touched.upload_document
                  }
                />

                <Form.Control.Feedback type="invalid">
                  {formik.errors.upload_document}
                </Form.Control.Feedback>
              </div>
            </Col>
          </Row>

          <hr />

          <StockTransferInwardTableForEdit
            dispatchItems={dispatchItems}
            onItemsScanned={handleItemsScanned}
            initialScannedItems={scannedItems}
            isEditMode={true}
          />

          <div className="d-flex justify-content-center gap-3 py-3">
            <CancelBackButton
              label="Cancel"
              onClick={() => {
                showConfirm(
                  "Any unsaved changes will be lost.",
                  "Are you sure?",
                  "Yes",
                  "No"
                ).then((result) => {
                  if (result.isConfirmed) {
                    navigate("/warehouse-item-inward");
                  }
                });
              }}
            />

            <SubmitButton
              isSubmitting={formik.isSubmitting}
              labels={{
                create: "Update GRN",
                submitting: "Updating GRN",
              }}
            />
          </div>
        </Form>
      )}
    </PageContainer>
  );
};

export default EditStockTransferGRN;
